import { Box, Group, Skeleton, Stack } from '@mantine/core';
import { IconAlertTriangle, IconCheck } from '@tabler/icons-react';
import { DeviceGetResponse } from 'api/actions/device-get/device-get-response';
import { useApi } from 'api/api-context';
import Toast from 'components/Toast';
import P1Regular from 'components/typography/P1Regular';
import P3Regular from 'components/typography/P3Regular';
import panic from 'errors/panic';
import { useEffect, useMemo, useState } from 'react';
import { useResponsiveDesign } from 'hooks/use-responsive-design';

interface ParentDeviceFaultsProps {
  parentDeviceId: string;
}

/**
 * Displays readonly faults configured on parent device.
 */
export default function ParentDeviceFaults({ parentDeviceId }: ParentDeviceFaultsProps) {
  const { isTablet } = useResponsiveDesign();
  const { getAction } = useApi();
  const [parentDevice, setParentDevice] = useState<DeviceGetResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const getDevice = getAction('DeviceGet');

    setLoading(true);

    getDevice({
      parameters: { deviceId: parentDeviceId },
    })
      .then((device) => {
        setParentDevice(device);
      })
      .catch(panic)
      .finally(() => {
        setLoading(false);
      });
  }, [getAction, parentDeviceId]);

  const parentFaults = useMemo(
    () => parentDevice?.faults?.filter((fault) => !fault.deleted) ?? [],
    [parentDevice?.faults]
  );

  if (loading) {
    return (
      <Stack ml={40} mr={24} spacing={16}>
        <Skeleton height={148} radius="sm" />
      </Stack>
    );
  }

  if (parentFaults.length === 0) {
    return (
      <Box ml={40} mr={24}>
        <Toast type="info" size="s" withCloseButton={false} message="Nadradené zariadenie nemá žiadne nedostatky." />
      </Box>
    );
  }

  return (
    <Stack spacing={16}>
      {parentFaults.map((fault, index) => {
        const isFixed = fault.fixed?.fixedAt !== undefined;

        return (
          <Box
            key={fault.faultId ?? `${fault.faultName}-${index}`}
            py={24}
            px={isTablet ? 20 : 40}
            ml={40}
            mr={24}
            bg="gray.0"
            sx={() => ({
              borderRadius: '4px',
            })}
          >
            <Group spacing={8} mb={16}>
              {isFixed ? (
                <IconCheck stroke="1.5" height={20} width={20} />
              ) : (
                <IconAlertTriangle stroke="1.5" height={20} width={20} />
              )}
              <P1Regular color="gray.8">{isFixed ? 'Vyriešený nedostatok' : 'Nevyriešený nedostatok'}</P1Regular>
            </Group>

            <Box
              sx={() => ({
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1fr',
                gap: '40px',
              })}
            >
              <Stack spacing={4}>
                <P3Regular color="gray.6">Názov</P3Regular>
                <P1Regular color="gray.8">{fault.faultName || null}</P1Regular>
              </Stack>

              <Stack spacing={4}>
                <P3Regular color="gray.6">Popis</P3Regular>
                <P1Regular color="gray.8">{fault.description || null}</P1Regular>
              </Stack>

              <Stack spacing={4}>
                <P3Regular color="gray.6">Dátum riešenia</P3Regular>
                <P1Regular color="gray.8">
                  {fault.fixed?.fixedAt ? new Date(fault.fixed.fixedAt).toLocaleDateString('sk') : null}
                </P1Regular>
              </Stack>

              <Stack spacing={4}>
                <P3Regular color="gray.6">Vyriešil</P3Regular>
                <P1Regular color="gray.8">
                  {fault.fixed
                    ? fault.fixed.fixedBy.fullName ?? `${fault.fixed.fixedBy.firstName} ${fault.fixed.fixedBy.lastName}`
                    : null}
                </P1Regular>
              </Stack>
              <Stack spacing={4}>
                <P3Regular color="gray.6">Zaznamenal riešenie</P3Regular>
                <P1Regular color="gray.8">
                  {fault.fixed
                    ? (fault.fixed.recordedBy ?? fault.fixed.fixedBy).fullName ??
                      `${(fault.fixed.recordedBy ?? fault.fixed.fixedBy).firstName} ${
                        (fault.fixed.recordedBy ?? fault.fixed.fixedBy).lastName
                      }`
                    : null}
                </P1Regular>
              </Stack>
              <Stack spacing={4}>
                <P3Regular color="gray.6">Popis riešenia</P3Regular>
                <P1Regular color="gray.8">{fault.fixed?.fixedNote || null}</P1Regular>
              </Stack>
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
}
